import React from 'react';
import * as styles from '../styles/notificationStyles';
import { Formatters, Messages } from '../../codegen/Localize';
import { NotificationIcon } from './NotificationIcon';
import { NotificationActions } from './NotificationActions';
import { Keys } from '../../ts/helpers/Keys';
import ReactMarkdown from 'react-markdown';
import { Tooltip } from '@tableau/widgets';
import { CSSProperties } from 'react';
import { SiteUrls } from '@tableau/shared-urls';
import { getProfileImageUrl, IUserNotification, IUserNotificationContentProvider } from '@tableau/user-notifications-client-core';
import * as TypeStyle from 'typestyle';
import _ from 'underscore';
import { isNullOrUndefined } from '../utility/notificationHelper';
import { errorBoundary, sendTelemetryErrorEvent } from '../../common/react/components/ErrorBoundary';
import { IUserNotificationMenuItemProvider } from '../menuProviders/IUserNotificationMenuItemProvider';
import { sendUserNotificationItemTelemetry, UserNotificationsTelemetryEvents } from '../telemetry/UserNotificationsTelemetry';

export interface NotificationProps {
  notification: IUserNotification<any>;
  notificationProvider: IUserNotificationContentProvider<any>;
  notificationMenuItemProvider: IUserNotificationMenuItemProvider<any>;
  siteUrls: SiteUrls;
  onKeyDown: (e: any) => void;
  onNotificationClick: () => void;
  closePanel: () => void;
  userId: string;
  siteLuid: string;
}

/**
 * Notification component representing a single notification
 */
export class UserNotification extends React.PureComponent<NotificationProps> {

  constructor(props: NotificationProps) {
    super(props);
  }

  // Check for invalid notification URL
  private invalidUrl = isNullOrUndefined(this.props.notification.targetInfo?.targetUrl);

  public render(): JSX.Element {
    const notification = this.props.notification;
    return (
      <div role='row' className='notification-row' style={styles.notificationItemRow} data-tb-test-id={`notification-row-${notification.id}`}>
        <div
          role='gridcell'
          data-tb-test-id={`notification-${notification.id}`}
          id={`notification-${notification.id}`}
          className={this.invalidUrl ? TypeStyle.classes(styles.notificationItemDisabled, 'disabled-notification') : styles.notificationItem}
          tabIndex={0}
          onClick={this.handleClick}
          onKeyDown={this.props.onKeyDown}
        >
          <div className={TypeStyle.classes(this.invalidUrl && styles.transparent, styles.notificationContainer, 'notification-container')}>
            <div style={styles.profileContainer}>
              <div className='notification-read-status' style={styles.blueDotCss(!notification.read)} />
              <NotificationIcon
                imageUrl={getProfileImageUrl(notification.profileImageId)}
                notificationType={notification.type}
                displayName={notification.senderDisplayName} />
            </div>
            <div style={styles.notificationContentContainer}>
              <div style={styles.contentHeader}>
                <span className='notification-create-time' style={styles.createdAt}>
                  {Formatters.formatMediumDateTimeWithoutSeconds(notification.creationTime)}
                </span>
              </div>
              {this.renderTitle()}
              {this.renderContent()}
            </div>
          </div>
          {this.renderErrorMessage()}
        </div>
        <NotificationActions
          notificationId={notification.id}
          notificationType={notification.type}
          actions={this.props.notificationMenuItemProvider.getNotificationMenuItems(notification, this.props.siteUrls)}
          onKeyDown={this.handleActionKeyDown}
          closePanel={this.props.closePanel}
          userId={this.props.userId}
          siteLuid={this.props.siteLuid} />
      </div>
    );
  }

  private renderErrorMessage(): JSX.Element {
    const errMsg = this.props.notificationProvider.getErrorMessage(this.props.notification);
    return <div className={TypeStyle.classes(styles.notificationError, 'notification-error-message')}>{errMsg}</div>;
  }
  //all links from markdown should be rendered as span to avoid double click handler
  private renderLink = (props: any) => {
    return <span className={styles.notificationFakeLink}>{props.children}</span>;
  };
  // this override method is added as a walkaround for TFS 1207709. 
  // We wrap special char encoding with <code> and displays the original format with span
  private renderCode = (props: any) => {
    return <span>{props.children}</span>;
  }

  private renderTitle(): JSX.Element {
    const titleText = this.props.notificationProvider.getNotificationTitle(this.props.notification);

    const title =
      <ReactMarkdown
        source={titleText}
        escapeHtml={true}
        renderers={this.invalidUrl ? { paragraph: 'div', link: 'span', inlineCode: this.renderCode } :
          { paragraph: 'div', link: this.renderLink, inlineCode: this.renderCode }} />;

    return this.renderWithTooltip(
      title,
      styles.notificationTitle,
      `usernotifiation-title-tooltip-${this.props.notification.id}`
    );
  }

  private renderContent(): JSX.Element {
    const lineCount = this.props.notificationProvider.getMaxShownContentLineCount();
    const contentText = this.props.notificationProvider.getNotificationContent(this.props.notification);
    const content =
      <ReactMarkdown
        source={contentText}
        escapeHtml={true}
        renderers={{ paragraph: 'div', link: this.renderLink, inlineCode: this.renderCode }} />;

    if (_.isUndefined(lineCount)) {
      return <div style={styles.notificationContent}>{content}</div>;
    }

    return this.renderWithTooltip(
      content,
      { ...styles.notificationContent, WebkitLineClamp: lineCount },
      `usernotifiation-content-tooltip-${this.props.notification.id}`);
  }

  private renderWithTooltip(content: JSX.Element, containerStyle: CSSProperties, tooltipTestId: string) {
    let elementRef: HTMLElement;

    const getTooltipContent = () => {
      if (elementRef.scrollHeight > elementRef.offsetHeight) {
        return <div style={styles.textWrap}> {content} </div>;
      } else {
        return null;
      }
    };

    return (
      <Tooltip getContent={() => getTooltipContent()} testId={tooltipTestId}>
        <div tabIndex={-1} ref={(ref) => elementRef = ref} style={containerStyle} className={styles.notificationTextContainerClass}>{content}</div>
      </Tooltip>
    );
  }

  private handleClick = (): void => {
    sendUserNotificationItemTelemetry(UserNotificationsTelemetryEvents.clickNotificationItem, this.props.userId, this.props.siteLuid, this.props.notification.type);
    this.props.onNotificationClick();
  }

  private handleActionKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = e => {
    if (e.keyCode === Keys.ArrowLeft) {
      e.preventDefault();
      e.stopPropagation();
      const selector = '.' + styles.notificationItem;
      const notificationItem = e.currentTarget.parentElement.parentElement.querySelector<HTMLDivElement>(selector);
      notificationItem.focus();
    } else if (e.keyCode === Keys.Tab) {
      e.preventDefault();
      e.stopPropagation();
      const selector = '.' + styles.notificationItem;
      const notificationItem = e.currentTarget.parentElement.parentElement.querySelector<HTMLDivElement>(selector);
      const notifications = e.currentTarget.parentElement.parentElement.parentElement.parentElement.querySelectorAll<HTMLDivElement>(selector);
      let index: number = Array.prototype.indexOf.call(notifications, notificationItem);
      if (index === notifications.length - 1) {
        index = 0;
      } else {
        index += 1;
      }
      notifications[index]?.focus();
    }
  }
}

export const Notification = errorBoundary<NotificationProps>(sendTelemetryErrorEvent)(UserNotification);